import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Church, Presentation, GraduationCap, User, 
  Users, BookOpen, Sparkles, ArrowRight, Check,
  ChevronLeft
} from 'lucide-react';

const USER_TYPES = [
  {
    id: 'church',
    title: 'Church',
    description: 'Manage worship services, create slideshows for sermons, and connect with your congregation',
    icon: Church,
    color: 'bg-amber-500',
    subcategories: [
      { id: 'admin', label: 'Church Admin / Pastor', icon: Users }
    ]
  },
  {
    id: 'educator',
    title: 'Educator',
    description: 'Create educational presentations, manage student communities, and share learning materials',
    icon: GraduationCap,
    color: 'bg-indigo-500',
    subcategories: [
      { id: 'teacher', label: 'Teacher / Instructor', icon: BookOpen }
    ]
  },
  {
    id: 'presenter',
    title: 'Presenter',
    description: 'Create professional slideshows for meetings, conferences, and events',
    icon: Presentation,
    color: 'bg-emerald-500',
    subcategories: null
  },
  {
    id: 'user',
    title: 'User',
    description: 'Join communities, access shared content, and create personal slideshows',
    icon: User,
    color: 'bg-sky-500',
    subcategories: [
      { id: 'student', label: 'Student', icon: GraduationCap },
      { id: 'pastor', label: 'Pastor / Ministry Team', icon: Church },
      { id: 'regular', label: 'Regular User', icon: User }
    ]
  }
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUserType, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  const [step, setStep] = useState(1); // 1 = select type, 2 = select subcategory
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if not authenticated - use effect to avoid state updates during render
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // If user already has type, redirect to dashboard
  useEffect(() => {
    if (user && user.user_type && !user.needs_onboarding) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Show nothing while redirecting
  if (!isAuthenticated) {
    return null;
  }

  if (user && user.user_type && !user.needs_onboarding) {
    return null;
  }

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    if (type.subcategories && type.subcategories.length > 0) {
      setStep(2);
    } else {
      // No subcategories, proceed directly
      handleComplete(type.id, null);
    }
  };

  const handleSubcategorySelect = (subcategory) => {
    setSelectedSubcategory(subcategory);
  };

  const handleComplete = async (typeId = selectedType?.id, subId = selectedSubcategory?.id) => {
    if (!typeId) {
      toast.error('Please select a user type');
      return;
    }

    setIsLoading(true);
    try {
      await setUserType(typeId, subId);
      toast.success('Profile setup complete!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedSubcategory(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-amber-500" />
            <span className="font-heading text-3xl font-bold text-white">SlideCon</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {step === 1 ? 'Tell us about yourself' : 'Almost there!'}
          </h1>
          <p className="text-slate-400">
            {step === 1 
              ? 'Choose the option that best describes how you\'ll use SlideCon'
              : `Great! Now select your specific role as ${selectedType?.title}`
            }
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex justify-center gap-2 mb-8">
          <div className={`w-20 h-1 rounded-full transition-colors ${step >= 1 ? 'bg-amber-500' : 'bg-slate-700'}`} />
          <div className={`w-20 h-1 rounded-full transition-colors ${step >= 2 ? 'bg-amber-500' : 'bg-slate-700'}`} />
        </div>

        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {USER_TYPES.map((type) => (
                <Card
                  key={type.id}
                  className={`cursor-pointer transition-all hover:scale-[1.02] bg-slate-800/50 border-slate-700 hover:border-amber-500/50 ${
                    selectedType?.id === type.id ? 'ring-2 ring-amber-500' : ''
                  }`}
                  onClick={() => handleTypeSelect(type)}
                  data-testid={`type-${type.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${type.color}`}>
                        <type.icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-white">{type.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-400">
                      {type.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Back button */}
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-slate-400 hover:text-white gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>

              {/* Selected type indicator */}
              <div className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-6">
                <div className={`p-2 rounded-lg ${selectedType?.color}`}>
                  {selectedType && <selectedType.icon className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedType?.title}</p>
                  <p className="text-sm text-slate-400">Select your specific role</p>
                </div>
              </div>

              {/* Subcategory options */}
              <div className="grid grid-cols-1 gap-3">
                {selectedType?.subcategories?.map((sub) => (
                  <Card
                    key={sub.id}
                    className={`cursor-pointer transition-all hover:scale-[1.01] bg-slate-800/50 border-slate-700 hover:border-amber-500/50 ${
                      selectedSubcategory?.id === sub.id ? 'ring-2 ring-amber-500 border-amber-500' : ''
                    }`}
                    onClick={() => handleSubcategorySelect(sub)}
                    data-testid={`subcategory-${sub.id}`}
                  >
                    <CardContent className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <sub.icon className="w-5 h-5 text-amber-500" />
                        <span className="text-white font-medium">{sub.label}</span>
                      </div>
                      {selectedSubcategory?.id === sub.id && (
                        <Check className="w-5 h-5 text-amber-500" />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Continue button */}
              <Button
                onClick={() => handleComplete()}
                disabled={!selectedSubcategory || isLoading}
                className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-black gap-2"
                data-testid="complete-onboarding"
              >
                {isLoading ? (
                  'Setting up your account...'
                ) : (
                  <>
                    Continue to Dashboard
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Welcome message for user */}
        {user && (
          <p className="text-center text-slate-500 text-sm mt-8">
            Welcome, {user.name}! 👋
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingPage;
